import { z } from 'zod';
import { defineForm } from '../registry.js';
import { CONFIRMERS, evidenceFields, longtext } from './common.js';

// 第1表（居宅サービス計画書(1)）。厚労省 記載要領・別紙1 の 16 項目。
// ①〜⑫は基本情報からの転記（読むときに作る。本文には確定時の写しだけ任意で持つ）、⑬〜⑯が手入力。
// 旧: apps/api/src/plan1-fields.ts ＋ Plan1FieldRecord

export interface Plan1FieldMaster {
  fieldCode: string;
  name: string;
  inputType: 'transcription' | 'text' | 'choice';
}

export const PLAN1_FIELDS = [
  { fieldCode: '01', name: '利用者名', inputType: 'transcription' },
  { fieldCode: '02', name: '生年月日', inputType: 'transcription' },
  { fieldCode: '03', name: '住所', inputType: 'transcription' },
  { fieldCode: '04', name: '居宅サービス計画作成者氏名', inputType: 'transcription' },
  { fieldCode: '05', name: '居宅介護支援事業者・事業所名/所在地', inputType: 'transcription' },
  { fieldCode: '06', name: '居宅サービス計画作成（変更）日', inputType: 'transcription' },
  { fieldCode: '07', name: '初回居宅サービス計画作成日', inputType: 'transcription' },
  { fieldCode: '08', name: '初回・紹介・継続', inputType: 'transcription' },
  { fieldCode: '09', name: '認定済・申請中', inputType: 'transcription' },
  { fieldCode: '10', name: '認定日', inputType: 'transcription' },
  { fieldCode: '11', name: '認定の有効期間', inputType: 'transcription' },
  { fieldCode: '12', name: '要介護状態区分', inputType: 'transcription' },
  { fieldCode: '13', name: '利用者及び家族の生活に対する意向を踏まえた課題分析の結果', inputType: 'text' },
  { fieldCode: '14', name: '介護認定審査会の意見及びサービスの種類の指定', inputType: 'text' },
  { fieldCode: '15', name: '総合的な援助の方針', inputType: 'text' },
  { fieldCode: '16', name: '生活援助中心型の算定理由', inputType: 'choice' },
] as const satisfies readonly Plan1FieldMaster[];

// 手入力（⑬⑭⑮⑯）
export const PLAN1_INPUT_FIELDS = PLAN1_FIELDS.filter((f) => f.inputType === 'text' || f.inputType === 'choice');
export const PLAN1_INPUT_FIELD_CODES = ['13', '14', '15', '16'] as const;
export type Plan1FieldCode = (typeof PLAN1_INPUT_FIELD_CODES)[number];

// AI 下書きの対象。旧 API は ⑬〜⑯ すべて受けた（Python のエージェントは ⑬⑮ だけ）。ここは API 側に合わせる
export const PLAN1_AI_FIELD_CODES: readonly Plan1FieldCode[] = PLAN1_INPUT_FIELD_CODES;

// ⑯ 生活援助中心型の算定理由
export const PLAN1_REASON_OPTIONS = [
  { value: '1', label: '一人暮らし' },
  { value: '2', label: '家族等が障害、疾病等' },
  { value: '3', label: 'その他' },
] as const;

const nameOf = (code: string) => PLAN1_FIELDS.find((f) => f.fieldCode === code)!.name;

// 本文のキーは text（旧 API と同じ。Python のエージェントは value で送り、chat-stream が text へ詰め替えていた）。
// 長さの上限は旧 API に無い（エージェントは 500）。ここは API 側に合わせ上限なし
const textField = (code: Plan1FieldCode) =>
  z
    .object({ text: longtext(nameOf(code)), ...evidenceFields })
    .strict()
    .optional()
    .meta({ order: Number(code) })
    .describe(`${code} ${nameOf(code)}`);

// ⑯ は選択（option）＋補足（text）。旧 API は option を選択肢と突き合わせていなかった。ここは '1'|'2'|'3' に絞る
const reasonField = z
  .object({
    text: z.string().describe('補足'),
    option: z
      .enum(['1', '2', '3'])
      .meta({ optionLabels: Object.fromEntries(PLAN1_REASON_OPTIONS.map((o) => [o.value, o.label])) })
      .optional()
      .describe('算定理由'),
    ...evidenceFields,
  })
  .strict()
  .optional()
  .meta({ order: 16 })
  .describe(`16 ${nameOf('16')}`);

// 確定時の①〜⑫の写し（旧 CarePlanHistoryEntry.plan1Snapshot.transcription）。後から当時の表紙を再現するため
export const plan1TranscriptionItemSchema = z
  .object({
    fieldCode: z.enum(['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']).describe('項目番号'),
    name: z.string().describe('項目名'),
    value: z.string().describe('値'),
  })
  .strict();

export const careplan1Schema = z
  .object({
    '13': textField('13'),
    '14': textField('14'),
    '15': textField('15'),
    '16': reasonField,
    transcription: z.array(plan1TranscriptionItemSchema).max(12).optional().meta({ order: 17 }).describe('①〜⑫ 転記値（確定時の写し）'),
  })
  .strict()
  .refine((v) => PLAN1_INPUT_FIELD_CODES.some((c) => v[c] !== undefined), { message: '第1表は ⑬〜⑯ のどれかが必要です' });
export type Careplan1Body = z.infer<typeof careplan1Schema>;

export const careplan1Form = defineForm({
  app: 'caremanager',
  docType: 'careplan1',
  name: '居宅サービス計画書(1)（第1表）',
  version: 1,
  schema: careplan1Schema,
  confirmers: CONFIRMERS,
  description: '①〜⑫は基本情報から転記、⑬〜⑯を手入力する',
});
